import { useEffect, useReducer, useState } from "react";
import type { U2000FormSBusinessHandlers, U2000FormStateHandlersReturn } from "../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import useU2000AxiosAuthenticated from "../../u2000Core/axios/U2000UseAxiosAuthenticated";

import U2000DefaultForm from "../../u2000Core/components/formControls/U2000DefaultForm";

import { useToast } from "../../u2000Core/context/toast/U2000ToastContext";
import { U2000HandleApiError } from "../../u2000Core/axios/U2000HandleApiError";
import { u2000IntFormState, useU2000FormStateHandlers } from "../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import U2000Tab from "../../u2000Core/components/tab/U2000Tab";
import { useU2000SaveHandlers } from "../../u2000Core/hooks/handler/U2000UseSaveHandlers";
import u2000FormState, { U2000FormStateEnumEditMode, type U2000FormState } from "../../u2000Core/reducer/formReducer/U2000FormState";
import { U2000LUStateEnumNextAction } from "../../u2000Core/reducer/formReducer/U2000LUState";
import { useU2000DeleteHandlers } from "../../u2000Core/hooks/handler/U2000UseDeleteHandlers";
import { U2000GridStateDeleteAction, type U2000GridStateNextRecordAfterDeleteReturn } from "../../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import { useU2104Context } from "../U2104Context";
import {
  doCreateAffiliationData,
  doDeleteAffiliationData,
  doGetAffiliationAuditsData,
  doGetaffiliationIdData,
  doGetSecretKeyAffiliationData,
  doImportAffiliationData,
  doUpdateAffiliationData,
  doUpdateAffiliationPwdData,
  emptyAffiliation,
  luHandleAppiError,
  type U2104Affiliation,
} from "../U2104Service";
import U2104OrganizationAffiliaitionDeleteDlg from "./common/U2104OrganizationAffiliationDeleteDlg";
import { useU2104UseGetOrganizatonAffiliaitionHandlers } from "./form/u2104Affiliation/U2104UseGetOrganizatonAffiliaitionHandlers";
import U2104AffiliationBody from "./form/u2104Affiliation/U2104AffilationBody";
import { U2104AffiliationFormToolBar } from "./form/u2104Affiliation/U2104AffiliationFormToolBar";
import { NS, U2104ROLES } from "../U2104";
import U2104AffiliationActionsBody from "./form/u2104Affiliation/u2104AffiliationActionsTab/U2104AffiationActionBody";
import U2104AffilaitionRolesBody from "./form/u2104Affiliation/u2104AffilaitionRolesTab/U2104AffliliationRoleBody";
import { useU2000AuthContext } from "../../u2000Core/context/auth/U2000UseAuthContext";
import U2000AuditPanelComponent from "../../u2000Core/components/audit/U2000AuditPanelComponent";
import { U2000GeneratePassword } from "../../u2000Core/tools/U2000StringUtils";
import { generateTemporaryId } from "../../u2000Core/tools/U2000IdUtils";
import { u2000IsValidEmail } from "../../u2000Core/tools/U2000EmailHelper";
import { useU2104GetSecretKeyHandlers } from "./form/u2104Affiliation/U2104UseGetSecretKeyHandlers";
import { u2104ImportUserHandler } from "./form/u2104Affiliation/U2104ImportAffiliationHandlers";
import U2104ImportAffiliationDialog from "./form/u2104Affiliation/U2104ImportAffilliationDlg";
import U2104AffiliationShowSecretKeyDlg from "./form/u2104Affiliation/U2104AffiliationShowSecretKeyDlg";
import { useU2104UseImportAffiliaitonHandlers } from "./form/u2104Affiliation/U2104UseImportAffiliaitonHandlers";
import { useU2000LogicalUnitContext } from "../../u2000Core/context/logicalUnit/U2000LogicalUnitContext";

// Interface à placer en dehors du composant pour éviter l'erreur de modificateur
export interface U2104AffiliationFormExtededHandlers extends U2000FormStateHandlersReturn<U2104Affiliation> {
  importUser: () => void;
  addNewUser: () => void;
  modifyUser: () => void;
  deleteUser: () => Promise<void>;
  resetPassword: () => void;
  showSecretKey: () => void;
}

// Composant principal pour le formulaire d'affiliation
const U2104AffiliationForm: React.FC = () => {
  // Parameters
  const { id, affiliationId } = useParams();
  const { hasRole } = useU2000AuthContext();
  const { setLogicalUnitBreadcrumb } = useU2000LogicalUnitContext();

  // manage form initialization state
  const [hasInitialized, setHasInitialized] = useState(false);

  // Form state and reducer for u2101LogicalUnitBL
  const [formState, formStateDispatch] = useReducer<U2000FormState, any>(u2000FormState, u2000IntFormState);

  // iport User state
  const [importUserFormState, importUserFormStateDispatch] = useReducer<U2000FormState, any>(u2000FormState, u2000IntFormState);

  // manage active tab state
  const [activeTab, setActiveTab] = useState<string | null>(null);

  // navigate et location
  const location = useLocation();
  const navigate = useNavigate();

  // Translation
  const { t } = useTranslation();
  const toast = useToast();

  // API Axios Client
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();

  // manage show secret key dialog state
  const [isDialogSecretKeyOpen, setIsDialogSecretKeyOpen] = useState(false);

  // LU Handler,  Search Handlers, Grid Handlers from Context
  const { luStateHandlers, searchStateHandlers, gridOrgAffiliationStateHandlers: gridOrgUserStateHandlers } = useU2104Context();

  // Form Handlers and default methods
  const validateForm = (affiliation: U2104Affiliation) => {
    const result: Record<string, string> = {};
    if (!affiliation.user.username || affiliation.user.username.trim() === "") {
      result["user.username"] = t("errUserNameRequired", { ns: NS });
    }
    if (!affiliation.user.firstName || affiliation.user.firstName.trim() === "") {
      result["user.firstName"] = t("errFirstNameRequired", { ns: NS });
    }
    if (!affiliation.user.lastName || affiliation.user.lastName.trim() === "") {
      result["user.lastName"] = t("errLastNameRequired", { ns: NS });
    }
    if (!affiliation.user.email || affiliation.user.email.trim() === "") {
      result["user.email"] = t("errEmailRequired", { ns: NS });
    }
    if (affiliation.user.email && !u2000IsValidEmail(affiliation.user.email)) {
      result["user.email"] = t("errorB0010", { ns: NS });
    }
    if (
      (u2104AffiliationFormStateExtendedHandlers.getFormEditMode() === U2000FormStateEnumEditMode.ADD || "resetPwd" in u2104AffiliationFormStateExtendedHandlers.getCustomAttributes()) &&
      (typeof affiliation.user.password !== "string" || affiliation.user.password.trim() === "")
    ) {
      result["user.password"] = t("errPasswordRequired", { ns: NS });
    }
    return result;
  };

  // Business logic for Organization Affiliation Form
  const u2104OrganizationAffiliationBL: U2000FormSBusinessHandlers<U2104Affiliation> = {
    newRecord: () => {
      const affiliation = { ...emptyAffiliation };
      affiliation.id = generateTemporaryId();
      affiliation.user.id = generateTemporaryId();
      affiliation.user.password = U2000GeneratePassword();
      affiliation.roles = [];
      affiliation.actions = [];
      return affiliation;
    },

    // validation du formulaire
    validateForm: validateForm,

    // sauvegarde du formulaire
    saveForme: async (organizationUser: U2104Affiliation, editMode: U2000FormStateEnumEditMode) => {
      const errors = validateForm(organizationUser);
      if (Object.keys(errors).length === 0) {
        handleSave.saveData(organizationUser, editMode);
      } else {
        u2104AffiliationFormStateExtendedHandlers.setErrors(errors);
      }
    },

    // annulation du formulaire
    cancelForm: () => {
      const user = u2104AffiliationFormStateExtendedHandlers.getOldData() as U2104Affiliation | null;
      if (user) {
        // Met à false checkedId pour chaque action
        if ("users" in user && Array.isArray((user as any).users)) {
          (user as any).users = (user as any).users.map((user: any) => ({ ...user, idChecked: false }));
        }
        u2104AffiliationFormStateExtendedHandlers.setData({ ...user });
      }
      u2104AffiliationFormStateExtendedHandlers.setCustomAttributes({});

      if (luStateHandlers.getActionOriginFrom() === "/U2104/ORGANIZATION") {
        navigate(`/U2104/${id}`, { replace: true });
      }
    },

    // gestion du changement d'enregistrement
    onChangeRecordForm: (organizationUser) => {
      if (organizationUser.id) {
        handleGet.getOrganizationUser(id!, organizationUser.id);
      }
    },

    // gestion de la sortie du formulaire
    onLeaveForm: () => {
      navigate(`/U2104/${id}`, { replace: true });
    },
  };

  // Handlers for Organization Affiliation Form
  const u2104AffiliationFormStateHandlers = useU2000FormStateHandlers<U2104Affiliation>({
    formState,
    formStateDispatch,
    businessHandlers: u2104OrganizationAffiliationBL,
  });

  // extended methods
  const u2104AffiliationFormStateExtendedHandlers: U2104AffiliationFormExtededHandlers = {
    ...u2104AffiliationFormStateHandlers,

    // import User
    importUser: () => {
      u2104IImportHandlers.importUser();
    },

    // add new User
    addNewUser: () => {
      u2104AffiliationFormStateExtendedHandlers.setEditMode(U2000FormStateEnumEditMode.ADD);
    },

    // modify User
    modifyUser: () => {
      u2104AffiliationFormStateExtendedHandlers.setEditMode(U2000FormStateEnumEditMode.EDIT);
    },
    // delete User
    deleteUser: async () => {
      handleDelete.handleDelete();
    },

    // reset Password
    resetPassword: () => {
      const affiliation = u2104AffiliationFormStateExtendedHandlers.getData();
      affiliation.user.password = U2000GeneratePassword();
      u2104AffiliationFormStateExtendedHandlers.setData(affiliation);
      u2104AffiliationFormStateExtendedHandlers.setCustomAttributes({ ...u2104AffiliationFormStateExtendedHandlers.getCustomAttributes(), resetPwd: true });
      u2104AffiliationFormStateExtendedHandlers.setEditMode(U2000FormStateEnumEditMode.EDIT);
    },

    // show Secret Key
    showSecretKey: () => {
      handleGetSecretKeyHandlers.GetSecretKey();
    },
  };

  // import User Handlers
  const u2104ImportUserBL: U2000FormSBusinessHandlers<U2104Affiliation> = {
    newRecord: () => {
      const affiliation = { ...emptyAffiliation };
      affiliation.id = generateTemporaryId();
      affiliation.user.id = generateTemporaryId();
      affiliation.roles = [];
      affiliation.actions = [];
      return affiliation;
    },

    // sauvegarde du formulaire
    saveForme: async (organizationUser: U2104Affiliation, _editMode: U2000FormStateEnumEditMode) => {
      handleImportAffiliation.importUser(id!, organizationUser);
    },

    // annulation du formulaire
    cancelForm: () => {
      u2104IImportHandlers.setDialogOpen(false);
      if (luStateHandlers.getActionOriginFrom() === "/U2104/ORGANIZATION") {
        navigate(`/U2104/${id}`, { replace: true });
      }
    },
  };

  // Form Handlers and default methods for import User
  const u2104ImportUserFormStateHandlers = useU2000FormStateHandlers<U2104Affiliation>({
    formState: importUserFormState,
    formStateDispatch: importUserFormStateDispatch,
    businessHandlers: u2104ImportUserBL,
  });

  // Get Handlers for Organization Affiliation
  const u2104IImportHandlers = u2104ImportUserHandler({
    formStateHandlers: u2104ImportUserFormStateHandlers,
    u2000AxiosAuthenticated,
    t,
    toast,
  });

  // Call Get Handlers for Logical Unit
  const handleGet: any = useU2104UseGetOrganizatonAffiliaitionHandlers<U2104Affiliation & { id: string; affiliationId: string }>({
    doGetFn: (id: string, affiliationId: string) => {
      return doGetaffiliationIdData(u2000AxiosAuthenticated, id, affiliationId);
    },

    onSuccess: (affiliation: U2104Affiliation & { id: string; affiliationId: string }) => {
      gridOrgUserStateHandlers.setDataById(affiliation.id, affiliation);
      u2104AffiliationFormStateExtendedHandlers.setData(affiliation);

      // Remplace l'id dans l'URL par le nouvel id
      const newPath = location.pathname.replace(/(?:new|[\w-]+)$/, String(affiliation.id));
      navigate(newPath, { replace: true });

      setLogicalUnitBreadcrumb([
        { href: "/U2104", label: t("title", { ns: NS }) },
        { href: `/U2104/${affiliation.organization.id}`, label: affiliation.organization.organizationName },
        { label: affiliation.user?.firstName + " " + affiliation.user?.lastName + " (" + affiliation.user?.username + ")" },
      ]);

      // Détermine le mode du formulaire selon l'action précédente
      if (luStateHandlers.getNextAction() === U2000LUStateEnumNextAction.MODIFY) {
        u2104AffiliationFormStateExtendedHandlers.setEditMode(U2000FormStateEnumEditMode.EDIT);
      }
    },

    onError: (error: any) => {
      const errorResult = U2000HandleApiError(error, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
  });

  // Call Save Handlers for Logical Unit
  const handleSave = useU2000SaveHandlers<U2104Affiliation>({
    doSaveFn: (affiliation: U2104Affiliation, editMode: U2000FormStateEnumEditMode) => {
      if (editMode === U2000FormStateEnumEditMode.ADD) {
        return doCreateAffiliationData(u2000AxiosAuthenticated, id!, affiliation);
      } else if (editMode === U2000FormStateEnumEditMode.EDIT) {
        if ("resetPwd" in u2104AffiliationFormStateExtendedHandlers.getCustomAttributes()) {
          return doUpdateAffiliationPwdData(u2000AxiosAuthenticated, id!, affiliation.id, affiliation);
        }
        return doUpdateAffiliationData(u2000AxiosAuthenticated, id!, affiliation.id, affiliation);
      }
      // Ensure a Promise is always returned to satisfy the handler's expected signature
      return Promise.resolve(undefined);
    },
    onSuccess: (affiliation: U2104Affiliation & { id: string }, editMode: U2000FormStateEnumEditMode) => {
      u2104AffiliationFormStateExtendedHandlers.setCustomAttributes({});
      if (editMode === U2000FormStateEnumEditMode.ADD) {
        gridOrgUserStateHandlers.setData([...gridOrgUserStateHandlers.getData(), affiliation]);
        gridOrgUserStateHandlers.setTotalRows(gridOrgUserStateHandlers.getTotalRows() + 1);
        gridOrgUserStateHandlers.setCurrentIdx(gridOrgUserStateHandlers.getData().length);

        // Remplace l'id dans l'URL par le nouvel id
        const newPath = location.pathname.replace(/(?:new|[\w-]+)$/, String(affiliation.id));
        navigate(newPath, { replace: true });
      } else if (editMode === U2000FormStateEnumEditMode.EDIT) {
        gridOrgUserStateHandlers.setDataById(affiliation.id, affiliation);
      }
      u2104AffiliationFormStateExtendedHandlers.afterSaveData(affiliation);
      toast.success(t("msgAffiliationSaved", { ns: NS }));

      // Retourne à la liste après l'ajout ou la modification selon l'origine
      if (luStateHandlers.getNextAction() === U2000LUStateEnumNextAction.ADD || luStateHandlers.getNextAction() === U2000LUStateEnumNextAction.MODIFY) {
        if (luStateHandlers.getActionOriginFrom() === "/U2104/ORGANIZATION") {
          navigate(`/U2104/${id}`, { replace: true });
        }
      }
    },
    onError: (error: any) => {
      const errorResult = U2000HandleApiError(error, t, NS, luHandleAppiError);

      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      } else if (errorResult.otherErrors && Object.keys(errorResult.otherErrors).length > 0) {
        u2104AffiliationFormStateExtendedHandlers.setOtherErrors(errorResult.otherErrors);
      } else if (errorResult.fieldErrors && Object.keys(errorResult.fieldErrors).length > 0) {
        u2104AffiliationFormStateExtendedHandlers.setErrors(errorResult.fieldErrors);
      }
    },
  });

  // Call Import User Handlers for Logical Unit
  const handleImportAffiliation = useU2104UseImportAffiliaitonHandlers<U2104Affiliation & { id: string }>({
    doImportFn: (orgId: string, affiliation: U2104Affiliation) => {
      return doImportAffiliationData(u2000AxiosAuthenticated, orgId, affiliation);
    },
    onSuccess: (affiliation: U2104Affiliation & { id: string }) => {
      gridOrgUserStateHandlers.setData([...gridOrgUserStateHandlers.getData(), affiliation]);
      gridOrgUserStateHandlers.setTotalRows(gridOrgUserStateHandlers.getTotalRows() + 1);
      gridOrgUserStateHandlers.setCurrentIdx(gridOrgUserStateHandlers.getData().length);
      u2104AffiliationFormStateExtendedHandlers.afterSaveData(affiliation);
      toast.success(t("msgLogicalUnitSaved", { ns: NS }));
      u2104AffiliationFormStateExtendedHandlers.setEditMode(U2000FormStateEnumEditMode.EDIT);
      u2104IImportHandlers.setDialogOpen(false);
    },
    onError: ({ error }) => {
      const errorResult = U2000HandleApiError(error, t, NS, luHandleAppiError);

      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
  });

  const handleDelete = useU2000DeleteHandlers({
    getSelectedRows: () => [u2104AffiliationFormStateExtendedHandlers.getData()],
    doDeleteFn: async (roleId, row) => {
      return await doDeleteAffiliationData(u2000AxiosAuthenticated, id!, roleId, row);
    },

    onSuccess: (deletedRows) => {
      let newData = gridOrgUserStateHandlers.getData();
      let currentIdx = gridOrgUserStateHandlers.getCurrentIdx();
      let totalRows = gridOrgUserStateHandlers.getTotalRows();

      deletedRows.forEach((row) => {
        toast.success(t("success0003", { key: (row as U2104Affiliation)?.user?.username ?? "", ns: NS }), t("successTitle", { ns: NS }));
        newData = newData.filter((dataRow) => dataRow.id !== row?.id);
        totalRows -= 1;
      });
      gridOrgUserStateHandlers.setData(newData);
      gridOrgUserStateHandlers.setTotalRows(totalRows);

      // Apres suppression, aller au prochain record, aller vers l'avant si possible, sinon aller vers l'arrière, si aucun, retourner à la liste
      const action: U2000GridStateNextRecordAfterDeleteReturn = gridOrgUserStateHandlers.getNextRecordAfterDeleteOnGridWithoutPagination(currentIdx, newData, totalRows);
      if (action.action === U2000GridStateDeleteAction.RECORD && action.id) {
        handleGet.getOrganizationUser(id!, action.id);
      } else if (action.action === U2000GridStateDeleteAction.LIST) {
        navigate(`/U2104/${id}`, { replace: true });
      }
    },

    onError: (errors) => {
      errors.forEach(({ error }) => {
        const errorResult = U2000HandleApiError(error, t, NS, luHandleAppiError);
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
      });
    },
  });

  const handleGetSecretKeyHandlers = useU2104GetSecretKeyHandlers<U2104Affiliation>({
    doGetSecretKeyFn: () => {
      // doLoadLogicalUnitActions expects one argument; provide a typed dummy value to satisfy the signature.
      return doGetSecretKeyAffiliationData(u2000AxiosAuthenticated, id!, u2104AffiliationFormStateExtendedHandlers.getData()?.id || "");
    },
    onSuccess: (affiliatiuon: U2104Affiliation) => {
      if (affiliatiuon) {
        u2104AffiliationFormStateExtendedHandlers.setData({
          ...u2104AffiliationFormStateExtendedHandlers.getData(),
          user: { ...u2104AffiliationFormStateExtendedHandlers.getData()?.user, secretKey: affiliatiuon.user.secretKey },
        });
        setIsDialogSecretKeyOpen(true);
      }
    },
    onError: ({ error }) => {
      // Gérer les erreurs lors du chargement des actions d'une unité logique
      console.error("Error loading logical unit actions:", error);
    },
  });

  //Wrapper adapté à U2000AuditPanelComponent: (axiosInstance, id) => Promise<...>
  const doGetAffiliationAuditsWrapper = (axiosInstance: any, affiliationId: string | number) => {
    return doGetAffiliationAuditsData(axiosInstance, id!, affiliationId!);
  };

  // Initialisation de lors du chargement de l'élément
  useEffect(() => {
    if (id && affiliationId && !hasInitialized) {
      if (affiliationId === "import") {
        u2104IImportHandlers.importUser();
      } else if (affiliationId === "new") {
        u2104AffiliationFormStateExtendedHandlers.setEditMode(U2000FormStateEnumEditMode.ADD);
        const newAffiliation = { ...emptyAffiliation };
        newAffiliation.id = generateTemporaryId();
        newAffiliation.user.id = generateTemporaryId();
        u2104AffiliationFormStateExtendedHandlers.setData(newAffiliation);
      } else if (id && affiliationId) {
        handleGet.getOrganizationUser(id, affiliationId);
      } else {
        toast.error(t("errInvalidaffiliationId", { ns: NS }));
      }
    }
    setHasInitialized(true);
  }, [id, affiliationId, hasInitialized]);

  // Tabs du formulaire
  const formTabsComponent: any[] = [
    {
      id: "tabRole",
      label: t("tabRoles", { ns: NS }),
      content: <U2104AffilaitionRolesBody organizationId={id!} u2104AffiliationFormHandlers={u2104AffiliationFormStateExtendedHandlers} />,
    },
    {
      id: "tabAction",
      label: t("tabActions", { ns: NS }),
      content: <U2104AffiliationActionsBody u2104AffiliationFormHandlers={u2104AffiliationFormStateExtendedHandlers} />,
    },
    hasRole(U2104ROLES.AUDITS) && {
      id: "tabAudits",
      label: t("tabAudits", { ns: NS }),
      content: <U2000AuditPanelComponent id={u2104AffiliationFormStateExtendedHandlers.getData()?.id} doGetAuditsData={doGetAffiliationAuditsWrapper} toastName="U2104AffiliationAudit" />,
    },
  ].filter(Boolean);

  // Render
  return (
    <>
      <U2104ImportAffiliationDialog u2104ImportAffiliationHandler={u2104IImportHandlers} t={t} NS={NS} />

      <U2104OrganizationAffiliaitionDeleteDlg handleDelete={handleDelete} t={t} NS={NS} />
      <U2104AffiliationShowSecretKeyDlg
        isDialogSecretKeyOpen={isDialogSecretKeyOpen}
        secretKey={u2104AffiliationFormStateExtendedHandlers.getData()?.user.secretKey}
        onClose={() => setIsDialogSecretKeyOpen(false)}
        t={t}
        NS={NS}
      />

      <U2000DefaultForm
        actionsBtns={<U2104AffiliationFormToolBar formStateHandlers={u2104AffiliationFormStateExtendedHandlers as any} />}
        searchStateHandlers={searchStateHandlers}
        gridStateHandlers={gridOrgUserStateHandlers}
        formStateHandlers={u2104AffiliationFormStateExtendedHandlers as any}
      >
        <U2104AffiliationBody formStateHandlers={u2104AffiliationFormStateExtendedHandlers as any} t={t} />

        <U2000Tab className="mt-4" tabs={formTabsComponent} defaultTab="tabRole" activeTab={activeTab ?? "tabRole"} setActiveTab={(tab) => setActiveTab(typeof tab === "string" ? tab : String(tab))} />
      </U2000DefaultForm>
    </>
  );
};

export default U2104AffiliationForm;
